// src/contexts/AuthContext.tsx

/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import {
  signInWithRedirect,
  fetchAuthSession,
  getCurrentUser,
  signOut,
} from 'aws-amplify/auth';
import { AppConfig } from '../types/AppConfig';

interface AuthContextType {
  apiKey: string;
  isLocalRelay: boolean;
  useFabricAuth: boolean;
  username: string | undefined;
  relayServerUrl: string;
  usernameAvatar: () => JSX.Element;
  updateApiKey: (newKey: string) => void;
  resetApiKey: () => void;
  getClientConfig: () => { url: string } | { apiKey: string; dangerouslyAllowAPIKeyInBrowser: boolean };
  getFabricClientConfig: () => { url: string };
}

type Stage = 'development' | 'beta' | 'prod';

const LOCAL_RELAY_SERVER_URL: string = process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';
const inputLabel = 'OpenAI API Key'
const storageIdentifier = 'tmp::voice_api_key';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [stage, setStage] = useState<Stage | undefined>(undefined);
  const [fabricServerUrl, setFabricServerUrl] = useState<string | undefined>(undefined);

  const [apiKey, setApiKey] = useState<string>(() => {
    if (LOCAL_RELAY_SERVER_URL) return '';
    return localStorage.getItem(storageIdentifier) || '';
  });

  const isLocalRelay = Boolean(LOCAL_RELAY_SERVER_URL);
  const relayServerUrl = LOCAL_RELAY_SERVER_URL;
  const useFabricAuth = true;  // Use server-based api key

  // #####################################
  // Section 1: AWS Cognito
  // #####################################

  // Get current stage
  const getCurrentStage = (origin: string): Stage => {
    if (process.env.NODE_ENV === 'development') return process.env.REACT_APP_STAGE_NAME as Stage || 'development';
    if (origin.includes('beta')) return 'beta';
    return 'prod'
  };

  const configureAmplify = async () => {
    try {
      const redirectURL = window.location.origin;
      const currentStage = getCurrentStage(redirectURL)
      setStage(currentStage);

      const result = await fetch(`/aws-exports-${currentStage === 'development' ? 'beta' : currentStage}.json`);
      const awsExports = await result.json();

      Amplify.configure({
        ...awsExports,
        oauth: {
          ...awsExports.oauth,
          redirectSignIn: redirectURL,
          redirectSignOut: redirectURL
        }
      });

      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      if (urlParams.get("loginlocal") !== "true") {
        let authenticated = false;
        try {
          const { username } = await getCurrentUser();

          const { tokens: session } = await fetchAuthSession();
          if (username && session) {
            authenticated = true;
          }

        } catch (e) {
          authenticated = false;
          try {
            await signOut({ global: true });
          } catch (signOutError) {
            console.error('Cleanup error:', signOutError);
          }
        }

        if (!authenticated) {
          if (awsExports.config?.auth_federated_provider?.name) {
            await signInWithRedirect({
              provider: {
                custom: awsExports.config.auth_federated_provider.name
              }
            });
            return;
          } else {
            // Fall back to default redirect if no specific provider is configured
            await signInWithRedirect();
            return;
          }
        }
      }

      setConfig(awsExports);
      setIsConfigured(true);
    } catch (e) {
      console.error('Configuration error:', e);
    }
  };

  const checkAuthStatus = async () => {
    try {
      const { username } = await getCurrentUser();
      const cleanUsername = username.includes('_')
        ? username.split('_').pop()
        : username;
      console.log('Username:', cleanUsername);
      console.log('Stage:', stage);
      setIsAuthenticated(true);
      setUsername(cleanUsername);

      try {
        const session = await fetchAuthSession();
        console.log('Current session:', session);
      } catch (sessionErr) {
        console.error('Session error:', sessionErr);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setIsAuthenticated(false);
      setUsername(undefined);
    }
  };

  const usernameAvatar = useCallback(() => {
    return (
      <div className="profile-picture">
        <img
          src={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${username}`}
          alt={`${username}'s profile`}
        />
      </div>
    );
  }, [username])

  useEffect(() => {
    configureAmplify();
  }, []);

  useEffect(() => {
    if (isConfigured) {
      checkAuthStatus();
    }
  }, [isConfigured]);


  // #####################################
  // Section 2: Realtime API / Fabric
  // #####################################

  useEffect(() => {
    /**
     * Ask user for API Key
     * If we're using the local relay server, we don't need this
     */
    const initializeAuth = async () => {
      if (useFabricAuth) {
        localStorage.removeItem(storageIdentifier);
        return;
      }

      if (isLocalRelay) {
        return;
      }

      if (!apiKey) {
        const promptedKey = prompt(inputLabel);
        if (promptedKey) {
          setApiKey(promptedKey);
          localStorage.setItem(storageIdentifier, promptedKey);
        }
      }
    };

    initializeAuth();
  }, []);

  const updateApiKey = useCallback((newKey: string) => {
    if (newKey) {
      if (!useFabricAuth) {
        localStorage.setItem(storageIdentifier, newKey);
      }
      setApiKey(newKey);
    } else {
      localStorage.removeItem(storageIdentifier);
      setApiKey('');
    }
  }, []);

  /**
   * When you click the API key
   */
  const resetApiKey = useCallback(() => {
    const newKey = prompt(inputLabel);
    if (newKey !== null) {
      localStorage.removeItem(storageIdentifier);
      updateApiKey(newKey);
      window.location.reload();
    }
  }, []);

  const getClientConfig = useCallback(() => {
    if (isLocalRelay) {
      return { url: relayServerUrl };
    }
    return {
      apiKey,
      dangerouslyAllowAPIKeyInBrowser: true,
    };
  }, [isLocalRelay, relayServerUrl, apiKey]);

  useEffect(() => {
    if (!stage) return
    const override_server = process.env.REACT_APP_FABRIC_SERVER_URL
    if (override_server) {
      setFabricServerUrl(override_server);
      return;
    }
    switch (stage) {
      case 'development':
        setFabricServerUrl('localhost:8000');
        break;
      case 'beta':
        setFabricServerUrl('https://api-beta.fabric.ddg.amazon.dev');
        break;
      default:
        setFabricServerUrl('https://api.fabric.ddg.amazon.dev');
    }
  }, [stage])

  const getFabricClientConfig = useCallback(() => {
    return { url: (fabricServerUrl || '') }
  }, [fabricServerUrl]);

  return (
    <>
      {!isAuthenticated ? (
        <div />
      ) : (
        <Authenticator hideSignUp={true}>
          <AuthContext.Provider
            value={{
              apiKey,
              isLocalRelay,
              useFabricAuth,
              username,
              relayServerUrl,
              usernameAvatar,
              updateApiKey,
              resetApiKey,
              getClientConfig,
              getFabricClientConfig
            }}
          >
            {children}
          </AuthContext.Provider>
        </Authenticator>
      )
      }
    </>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
